import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import Header from "../../assets/Symposium-banner-2026.jpg";
import { useEffect, useState, useRef } from "react";
import axios from "axios";

const modules = [
  {
    title: "Security Scan",
    route: "/security-scan",
    description: "Scan visitor badges quickly at entry and exit points.",
    borderColor: "#1976d2",
    icon: "🔐",
  },
  {
    title: "Kiosk Badge Print",
    route: "/kiosk-print",
    description: "Print badges instantly from self-service kiosks.",
    borderColor: "#2e7d32",
    icon: "🖨️",
  },
  {
    title: "Search and Print",
    route: "/search-print",
    description: "Search visitors and print badges or details easily.",
    borderColor: "#d32f2f",
    icon: "🔍",
  },
];

// Format "2026-07-01" → "Jul 1"
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [, m, d] = dateStr.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[parseInt(m) - 1]} ${parseInt(d)}`;
};

// ── Stat Card ──
const StatCard = ({ icon, label, value, color, loading }) => (
  <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box
          sx={{
            bgcolor: `${color}18`,
            color,
            borderRadius: 2,
            p: 1.2,
            display: "flex",
            alignItems: "center",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={60} height={36} />
          ) : (
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value ?? "—"}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ── Custom Bar Chart (SVG, React 19 safe) ──
const CustomBarChart = ({ data, loading }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(600);

  useEffect(() => {
    const observe = () => {
      if (containerRef.current) setWidth(containerRef.current.offsetWidth);
    };
    observe();
    const ro = new ResizeObserver(observe);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (loading)
    return (
      <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
    );
  if (!data || data.length === 0)
    return (
      <Box
        height={300}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );

  const maxVal = Math.max(
    ...data.map((d) => Math.max(d.registered || 0, d.scanned || 0)),
    1,
  );
  const height = 260;
  const padding = { top: 20, right: 10, bottom: 50, left: 40 };
  const chartH = height - padding.top - padding.bottom;
  const chartW = width - padding.left - padding.right;
  const barGroupW = chartW / data.length;
  const barW = Math.min(barGroupW * 0.35, 20);
  const gap = barW * 0.3;

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(maxVal * p));

  const toY = (v) => chartH - ((v || 0) / maxVal) * chartH;

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <svg width={width} height={height} style={{ overflow: "visible" }}>
        <g transform={`translate(${padding.left},${padding.top})`}>
          {/* Y grid lines + labels */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line
                x1={0}
                y1={toY(t)}
                x2={chartW}
                y2={toY(t)}
                stroke="#e0e0e0"
                strokeDasharray="4 2"
              />
              <text
                x={-6}
                y={toY(t) + 4}
                textAnchor="end"
                fontSize={11}
                fill="#888"
              >
                {t}
              </text>
            </g>
          ))}

          {/* Bars */}
          {data.map((d, i) => {
            const cx = i * barGroupW + barGroupW / 2;
            const regH = Math.max(((d.registered || 0) / maxVal) * chartH, 1);
            const scanH = Math.max(((d.scanned || 0) / maxVal) * chartH, 1);
            const regX = cx - barW - gap / 2;
            const scanX = cx + gap / 2;
            return (
              <g key={i}>
                {/* Registered bar */}
                <rect
                  x={regX}
                  y={chartH - regH}
                  width={barW}
                  height={regH}
                  fill="#1976d2"
                  rx={3}
                  opacity={0.9}
                >
                  <title>{`${d.date}: Registered ${d.registered || 0}`}</title>
                </rect>
                {/* Scanned bar */}
                <rect
                  x={scanX}
                  y={chartH - scanH}
                  width={barW}
                  height={scanH}
                  fill="#2e7d32"
                  rx={3}
                  opacity={0.9}
                >
                  <title>{`${d.date}: Scanned ${d.scanned || 0}`}</title>
                </rect>
                {/* X label */}
                <text
                  x={cx}
                  y={chartH + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#555"
                >
                  {d.date}
                </text>
              </g>
            );
          })}

          {/* X axis line */}
          <line x1={0} y1={chartH} x2={chartW} y2={chartH} stroke="#bbb" />
        </g>
      </svg>
      {/* Legend */}
      <Box display="flex" gap={3} justifyContent="center" mt={1}>
        <Box display="flex" alignItems="center" gap={0.7}>
          <Box
            sx={{ width: 12, height: 12, bgcolor: "#1976d2", borderRadius: 1 }}
          />
          <Typography variant="caption">Registered</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.7}>
          <Box
            sx={{ width: 12, height: 12, bgcolor: "#2e7d32", borderRadius: 1 }}
          />
          <Typography variant="caption">Scanned</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ── Custom Area / Trend Chart (SVG, React 19 safe) ──
const CustomAreaChart = ({ data, loading }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(600);

  useEffect(() => {
    const observe = () => {
      if (containerRef.current) setWidth(containerRef.current.offsetWidth);
    };
    observe();
    const ro = new ResizeObserver(observe);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (loading)
    return (
      <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
    );
  if (!data || data.length === 0)
    return (
      <Box
        height={260}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );

  const maxVal = Math.max(
    ...data.map((d) => Math.max(d.registered || 0, d.scanned || 0)),
    1,
  );
  const height = 240;
  const padding = { top: 20, right: 10, bottom: 50, left: 40 };
  const chartH = height - padding.top - padding.bottom;
  const chartW = width - padding.left - padding.right;

  const toX = (i) => (i / (data.length - 1)) * chartW;
  const toY = (v) => chartH - ((v || 0) / maxVal) * chartH;

  const makePath = (key) =>
    data
      .map((d, i) => `${i === 0 ? "M" : "L"}${toX(i)},${toY(d[key] || 0)}`)
      .join(" ");

  const makeArea = (key, bottom) => {
    const line = makePath(key);
    const last = data.length - 1;
    return `${line} L${toX(last)},${bottom} L${toX(0)},${bottom} Z`;
  };

  const yTicks = [0, 0.5, 1].map((p) => Math.round(maxVal * p));

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <svg width={width} height={height} style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id="gReg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1976d2" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#1976d2" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="gScan" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2e7d32" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#2e7d32" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g transform={`translate(${padding.left},${padding.top})`}>
          {/* Y grid */}
          {yTicks.map((t, i) => (
            <g key={i}>
              <line
                x1={0}
                y1={toY(t)}
                x2={chartW}
                y2={toY(t)}
                stroke="#e0e0e0"
                strokeDasharray="4 2"
              />
              <text
                x={-6}
                y={toY(t) + 4}
                textAnchor="end"
                fontSize={11}
                fill="#888"
              >
                {t}
              </text>
            </g>
          ))}

          {/* Area fills */}
          <path d={makeArea("registered", chartH)} fill="url(#gReg)" />
          <path d={makeArea("scanned", chartH)} fill="url(#gScan)" />

          {/* Lines */}
          <path
            d={makePath("registered")}
            fill="none"
            stroke="#1976d2"
            strokeWidth={2.5}
            strokeLinejoin="round"
          />
          <path
            d={makePath("scanned")}
            fill="none"
            stroke="#2e7d32"
            strokeWidth={2.5}
            strokeLinejoin="round"
          />

          {/* Dots */}
          {data.map((d, i) => (
            <g key={i}>
              <circle
                cx={toX(i)}
                cy={toY(d.registered || 0)}
                r={3}
                fill="#1976d2"
              >
                <title>{`${d.date}: Registered ${d.registered || 0}`}</title>
              </circle>
              <circle cx={toX(i)} cy={toY(d.scanned || 0)} r={3} fill="#2e7d32">
                <title>{`${d.date}: Scanned ${d.scanned || 0}`}</title>
              </circle>
            </g>
          ))}

          {/* X labels (show every other for readability) */}
          {data.map(
            (d, i) =>
              i % 2 === 0 && (
                <text
                  key={i}
                  x={toX(i)}
                  y={chartH + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#555"
                >
                  {d.date}
                </text>
              ),
          )}

          {/* X axis */}
          <line x1={0} y1={chartH} x2={chartW} y2={chartH} stroke="#bbb" />
        </g>
      </svg>
      {/* Legend */}
      <Box display="flex" gap={3} justifyContent="center" mt={1}>
        <Box display="flex" alignItems="center" gap={0.7}>
          <Box
            sx={{ width: 12, height: 3, bgcolor: "#1976d2", borderRadius: 1 }}
          />
          <Typography variant="caption">Registered</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.7}>
          <Box
            sx={{ width: 12, height: 3, bgcolor: "#2e7d32", borderRadius: 1 }}
          />
          <Typography variant="caption">Scanned</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const Module = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // Always use today's date — auto-updates each time the dashboard loads
  const getToday = () => new Date().toISOString().split("T")[0];

  const fetchStats = async () => {
    setStatsLoading(true);
    const today = getToday();
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/form/stats?start=${today}&end=${today}`,
      );
      setStats(res.data);
    } catch (err) {
      console.error("Stats fetch error:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleCopy = (route) => {
    const basePath = window.location.pathname.split("/").slice(0, -1).join("/");
    const url = `${window.location.origin}${basePath}${route}`;
    navigator.clipboard.writeText(url);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/form/export-records`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "visitor-records.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert("Export failed. Please try again.");
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  const chartData = (stats?.series || []).map((d) => ({
    date: formatDate(d.date),
    registered: d.registered ?? 0,
    scanned: d.scanned ?? 0,
  }));

  const todaySeries = stats?.series?.slice(-1)[0];

  return (
    <Box
      sx={{
        paddingY: 2,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Banner + Controls */}
      <Box
        sx={{ width: "100%", maxWidth: 1200, px: { xs: 1, sm: 2 }, mx: "auto" }}
      >
        <Box
          component="img"
          src={Header}
          alt="Symposium Banner"
          sx={{
            width: "100%",
            height: "auto",
            maxHeight: { xs: 200, sm: 250, md: 300 },
            borderRadius: 2,
            mb: 2,
            objectFit: "cover",
          }}
        />
        <Box display="flex" justifyContent="flex-end" gap={2} mb={3}>
          <Button
            variant="contained"
            color="info"
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? "Exporting…" : "Export Records"}
          </Button>
          <Button variant="contained" color="warning" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </Box>

      <Box
        sx={{ width: "100%", maxWidth: 1200, px: 2, flexGrow: 1, mx: "auto" }}
      >
        {/* ── Stats Section ── */}
        <Typography variant="h5" fontWeight="bold" textAlign="center" mb={3}>
          📊 Dashboard Analytics
        </Typography>

        {/* Summary Stat Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              icon={<PeopleAltIcon />}
              label="Total Registered"
              value={stats?.totalRegistered}
              color="#1976d2"
              loading={statsLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              icon={<QrCodeScannerIcon />}
              label="Total Scanned"
              value={stats?.totalScanned}
              color="#2e7d32"
              loading={statsLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              icon={<TrendingUpIcon />}
              label="Registered Today"
              value={todaySeries?.registered ?? 0}
              color="#ed6c02"
              loading={statsLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              icon={<QrCodeScannerIcon />}
              label="Scanned Today"
              value={todaySeries?.scanned ?? 0}
              color="#9c27b0"
              loading={statsLoading}
            />
          </Grid>
        </Grid>
      </Box>
      {/* Module Cards */}
      <Box
        sx={{ width: "100%", maxWidth: 1200, px: 2, flexGrow: 1, mx: "auto" }}
      >
        <Typography variant="h5" fontWeight="bold" textAlign="center" mb={3}>
          Modules
        </Typography>
        <Grid container spacing={3} justifyContent="center" mb={5}>
          {modules.map((mod, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                onClick={() => navigate(mod.route)}
                sx={{
                  borderLeft: `6px solid ${mod.borderColor}`,
                  borderRadius: 3,
                  boxShadow: 4,
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "scale(1.02)", boxShadow: 8 },
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography fontSize={24}>{mod.icon}</Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {mod.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {mod.description}
                  </Typography>
                  <Box display="flex" justifyContent="center">
                    <Tooltip title="Copy URL">
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(mod.route);
                        }}
                      >
                        Copy & Share URL
                      </Button>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Today's date label */}
        {/* <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Showing stats for: <strong>{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>
          </Typography>
        </Box> */}

        {/* Bar Chart */}
        {/* <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4, p: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2} px={1}>
            Daily Registrations vs Scans
          </Typography>
          <CustomBarChart data={chartData} loading={statsLoading} />
        </Card> */}

        {/* Today: Visitors vs Repeated donut chart */}
        {/* <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4, p: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2} px={1}>
            Today's Visitors
          </Typography>
          {statsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={220}>
              <Typography color="text.secondary">Loading…</Typography>
            </Box>
          ) : (() => {
            const visitors = stats?.todayVisitors ?? 0;
            const repeated = stats?.todayRepeated ?? 0;
            const total = visitors + repeated;
            if (total === 0) {
              return (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={220} gap={1}>
                  <Typography color="text.secondary">No scans recorded today</Typography>
                </Box>
              );
            }
            // SVG donut
            const r = 70, cx = 110, cy = 110, stroke = 44;
            const circ = 2 * Math.PI * r;
            const visitorArc = (visitors / total) * circ;
            const repeatedArc = (repeated / total) * circ;
            return (
              <Box display="flex" alignItems="center" gap={4} flexWrap="wrap" justifyContent="center">
                <svg width={220} height={220} viewBox="0 0 220 220">
                  <circle
                    cx={cx} cy={cy} r={r}
                    fill="none" stroke="#2e7d32" strokeWidth={stroke}
                    strokeDasharray={`${repeatedArc} ${circ}`}
                    strokeDashoffset={0}
                    transform={`rotate(-90 ${cx} ${cy})`}
                  />
                  <circle
                    cx={cx} cy={cy} r={r}
                    fill="none" stroke="#1976d2" strokeWidth={stroke}
                    strokeDasharray={`${visitorArc} ${circ}`}
                    strokeDashoffset={-repeatedArc}
                    transform={`rotate(-90 ${cx} ${cy})`}
                  />
                  <text x={cx} y={cy - 8} textAnchor="middle" fontSize={28} fontWeight="bold" fill="#333">{total}</text>
                  <text x={cx} y={cy + 14} textAnchor="middle" fontSize={12} fill="#888">Total Scans</text>
                </svg>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 16, height: 16, bgcolor: '#1976d2', borderRadius: '50%' }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>New Visitors</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#1976d2">{visitors}</Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 16, height: 16, bgcolor: '#2e7d32', borderRadius: '50%' }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Repeated Visitors</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#2e7d32">{repeated}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Card> */}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 2,
          width: "100%",
          textAlign: "center",
          borderTop: "1px solid #ccc",
          mt: "auto",
          bgcolor: "#fff",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          &copy; IMTMA {new Date().getFullYear()}. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Module;
